import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Button,
  Text,
  VStack,
  HStack,
  Box,
  Icon,
  useBreakpointValue,
  Flex,
} from '@chakra-ui/react';

import { Workflow } from 'lucide-react';

const TourWelcomeModal = ({ open, onStart, onSkip, onRemindLater }) => {
  const isMobile = useBreakpointValue({ base: true, md: false });

  return (
    <Modal
      isOpen={open}
      onClose={onSkip}
      isCentered
      size={isMobile ? 'sm' : 'md'}
      motionPreset="slideInBottom"
    >
      <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(8px)" />

      <ModalContent
        bg="white"
        borderRadius="24px"
        px={{ base: 5, md: 8 }}
        py={{ base: 6, md: 8 }}
        mx={4}
        position="relative"
        overflow="hidden"
        boxShadow="0 25px 60px rgba(0,0,0,0.15)"
      >
        {/* Decorative Background */}
        <Box
          position="absolute"
          top="-60px"
          right="-40px"
          w="180px"
          h="180px"
          bg="blue.50"
          borderRadius="full"
        />

        <Box
          position="absolute"
          bottom="-70px"
          left="-50px"
          w="220px"
          h="220px"
          bg="purple.50"
          borderRadius="full"
        />

        <ModalBody p={0}>
          <VStack spacing={6} textAlign="center">
            {/* Main Icon */}
            <Box
              bgGradient="linear(to-r, blue.500, purple.500)"
              p={5}
              borderRadius="full"
              color="white"
              boxShadow="lg"
            >
              <Icon as={Workflow} boxSize={8} />
            </Box>

            {/* Heading */}
            <VStack spacing={2}>
              <Text
                fontSize={{ base: '2xl', md: '3xl' }}
                fontWeight="800"
                color="gray.800"
              >
                Welcome to NUBAXPULSE
              </Text>

              <Text
                fontSize={{ base: 'sm', md: 'md' }}
                color="gray.500"
                fontWeight="500"
              >
                Agile Project & Task Management Workspace
              </Text>
            </VStack>

            {/* Workflow Visual */}
            <Box w="100%" py={2}>
              <Flex
                align="center"
                justify="space-between"
                position="relative"
              >
                {/* Connector */}
                <Box
                  position="absolute"
                  left="50px"
                  right="50px"
                  top="22px"
                  h="3px"
                  bg="gray.200"
                  zIndex={0}
                />

                {/* Backlog */}
                <VStack spacing={2} zIndex={1}>
                  <Box
                    bg="orange.100"
                    color="orange.600"
                    w="48px"
                    h="48px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="full"
                    fontSize="22px"
                    fontWeight="bold"
                  >
                    📋
                  </Box>

                  <Text fontSize="xs" fontWeight="600">
                    Backlog
                  </Text>
                </VStack>

                {/* In Progress */}
                <VStack spacing={2} zIndex={1}>
                  <Box
                    bg="blue.100"
                    color="blue.600"
                    w="48px"
                    h="48px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="full"
                    fontSize="22px"
                    fontWeight="bold"
                  >
                    ⚙️
                  </Box>

                  <Text fontSize="xs" fontWeight="600">
                    In Progress
                  </Text>
                </VStack>

                {/* Done */}
                <VStack spacing={2} zIndex={1}>
                  <Box
                    bg="green.100"
                    color="green.600"
                    w="48px"
                    h="48px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    borderRadius="full"
                    fontSize="22px"
                    fontWeight="bold"
                  >
                    ✅
                  </Box>

                  <Text fontSize="xs" fontWeight="600">
                    Done
                  </Text>
                </VStack>
              </Flex>
            </Box>

            {/* Description */}
            <Text
              fontSize={{ base: 'sm', md: 'md' }}
              color="gray.600"
              maxW="420px"
              lineHeight="tall"
            >
              Manage projects, organize tasks, collaborate with your team,
              and track work seamlessly from planning to completion.
            </Text>

            {/* Feature Tags */}
            <HStack
              spacing={3}
              flexWrap="wrap"
              justify="center"
            >
              <Box
                px={3}
                py={1}
                bg="blue.50"
                color="blue.600"
                borderRadius="full"
                fontSize="sm"
                fontWeight="600"
              >
                Projects
              </Box>

              <Box
                px={3}
                py={1}
                bg="purple.50"
                color="purple.600"
                borderRadius="full"
                fontSize="sm"
                fontWeight="600"
              >
                Tasks
              </Box>

              <Box
                px={3}
                py={1}
                bg="green.50"
                color="green.600"
                borderRadius="full"
                fontSize="sm"
                fontWeight="600"
              >
                Workflows
              </Box>
            </HStack>

            {/* Buttons */}
            <HStack
              spacing={3}
              pt={2}
              w="100%"
              flexDirection={{ base: 'column', sm: 'row' }}
            >
              <Button
                w="100%"
                size="lg"
                borderRadius="12px"
                bgGradient="linear(to-r, blue.500, purple.500)"
                color="white"
                onClick={onStart}
                _hover={{
                  bgGradient: 'linear(to-r, blue.600, purple.600)',
                  transform: 'translateY(-2px)',
                }}
                transition="all .2s"
              >
                Start Tour
              </Button>

              <Button
                w="100%"
                size="lg"
                variant="outline"
                borderRadius="12px"
                onClick={onSkip}
              >
                Skip
              </Button>

              <Button
                w="100%"
                size="lg"
                variant="ghost"
                borderRadius="12px"
                onClick={onRemindLater}
              >
                Remind Later
              </Button>
            </HStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default TourWelcomeModal;